<template>
  <div class="animated fadeIn">
    <div class="row">
      <div class="col-sm-12">
        <div class="card">
          <header class="card-header">
            <strong>{{ headerTitle }}</strong>
          </header>

          <div v-if="loading" class="loading-container">
            <RotateSquare class="loading-position animated fadeIn" size="60px" />
          </div>

          <div v-else class="card-body p-0">
            <b-tabs card>

              <!-- ── Aba 1: Configuração ──────────────────────────────────── -->
              <b-tab title="Configuração" active>
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <div class="d-flex align-items-center">
                    <span class="text-muted small mr-2">Edição atual:</span>
                    <b-badge variant="primary" class="mr-1">Edição {{ form.edition_number }}</b-badge>
                    <span v-if="form.period_label" class="text-muted small">{{ form.period_label }}</span>
                  </div>
                  <button
                    v-if="form.is_current"
                    class="btn btn-outline-secondary btn-sm"
                    @click="abrirModalNovaEdicao"
                  >
                    <i class="fa fa-history mr-1"></i> Nova Edição
                  </button>
                </div>
                <div class="row">
                  <div class="col-lg-7">
                    <div class="form-group">
                      <label><strong>Título do formulário</strong></label>
                      <b-form-input
                        v-model="form.title"
                        placeholder="Título exibido para os respondentes"
                      />
                    </div>
                    <div class="form-group">
                      <label><strong>Texto introdutório</strong></label>
                      <b-form-textarea
                        v-model="form.description"
                        rows="5"
                        placeholder="Explique o objetivo do formulário para os participantes..."
                      />
                    </div>
                    <div class="form-group">
                      <label><strong>Status do formulário</strong></label>
                      <b-form-select v-model="form.status" :options="statusOpcoes" />
                      <small class="text-muted d-block mt-1">
                        <b-badge variant="secondary">Rascunho</b-badge> — invisível ao público.&nbsp;
                        <b-badge variant="success">Ativo</b-badge> — aceitando respostas.&nbsp;
                        <b-badge variant="danger">Encerrado</b-badge> — não aceita novas respostas.
                      </small>
                    </div>
                    <button class="btn btn-primary" :disabled="salvandoForm" @click="salvarForm">
                      {{ salvandoForm ? "Salvando..." : "Salvar configurações" }}
                    </button>
                  </div>

                  <div class="col-lg-5">
                    <div class="card bg-light border">
                      <div class="card-body">
                        <h6 class="mb-3">
                          <i class="fa fa-link mr-1"></i> Link público do formulário
                        </h6>
                        <div class="mb-2">
                          Status atual:
                          <b-badge :variant="statusVariant(form.status)" class="ml-1">
                            {{ statusLabel(form.status) }}
                          </b-badge>
                        </div>
                        <div class="input-group mb-2">
                          <b-form-input :value="publicUrl" readonly size="sm" />
                          <div class="input-group-append">
                            <button class="btn btn-outline-secondary btn-sm" @click="copiarLink" title="Copiar link">
                              <i class="fa fa-copy"></i>
                            </button>
                          </div>
                        </div>
                        <small class="text-muted">
                          Compartilhe este link para que a população possa responder.
                          O formulário só aceita respostas quando estiver <strong>Ativo</strong>.
                        </small>
                      </div>
                    </div>

                    <div class="card border mt-3">
                      <div class="card-body text-center">
                        <p class="mb-2 text-muted small">Perguntas base do sistema (fixas)</p>
                        <h3 class="mb-0 text-primary">14</h3>
                        <small class="text-muted">perguntas padrão PMAU</small>
                        <hr />
                        <p class="mb-2 text-muted small">Perguntas extras do município</p>
                        <h3 class="mb-0 text-success">{{ form.custom_questions ? form.custom_questions.length : 0 }}</h3>
                        <small class="text-muted">adicionadas pelo técnico</small>
                      </div>
                    </div>
                  </div>
                </div>
              </b-tab>

              <!-- ── Aba 2: Histórico de Edições ────────────────────────────── -->
              <b-tab title="Histórico de Edições">
                <div v-if="loadingEdicoes" class="text-center py-4">
                  <RotateSquare size="40px" />
                </div>
                <div v-else-if="edicoes.length <= 1" class="text-center py-5 text-muted">
                  <i class="fa fa-history fa-2x mb-3 d-block"></i>
                  Nenhuma edição anterior — esta é a edição inicial do formulário.
                </div>
                <div v-else>
                  <div v-for="e in edicoes" :key="e.id" class="card mb-2">
                    <div class="card-body py-3 px-3">
                      <div class="d-flex justify-content-between align-items-start">
                        <p class="mb-3 font-weight-bold">
                          Edição {{ e.edition_number }}
                          <b-badge v-if="e.is_current" variant="primary" class="ml-1" style="font-size:0.65rem">atual</b-badge>
                        </p>
                        <b-badge :variant="edicaoStatusVariant(e.status)">{{ edicaoStatusLabel(e.status) }}</b-badge>
                      </div>
                      <div class="small">
                        <div class="mb-1"><span class="text-muted">Período:</span> {{ e.period_label || "—" }}</div>
                        <div class="mb-1"><span class="text-muted">Respostas coletadas:</span> {{ e.response_count }}</div>
                        <div class="mb-1"><span class="text-muted">Aberto em:</span> {{ formatarData(e.created_at) }}</div>
                        <div><span class="text-muted">Encerrado em:</span> {{ e.closed_at ? formatarData(e.closed_at) : "—" }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </b-tab>

              <!-- ── Aba 3: Perguntas Fixas ───────────────────────────────── -->
              <b-tab title="Perguntas Fixas">
                <div v-if="loadingBaseQuestions" class="text-center py-4">
                  <RotateSquare size="40px" />
                </div>
                <div v-else>
                  <!-- Identificação -->
                  <h6 class="text-uppercase text-muted mb-3" style="font-size:0.72rem;letter-spacing:0.06em">
                    <i class="fa fa-user mr-1"></i> Identificação
                  </h6>
                  <div v-for="(q, i) in perguntasIdentificacao" :key="q.code" class="card mb-2">
                    <div class="card-body py-3 px-3">
                      <p class="mb-3 font-weight-bold">{{ i + 1 }}. {{ q.question_text }}</p>
                      <div class="small">
                        <div class="mb-1"><span class="text-muted">Tipo da questão:</span> {{ tipoLabel(q.question_type) }}</div>
                        <div class="mb-1"><span class="text-muted">Obrigatória:</span> {{ q.is_required ? 'Sim' : 'Não' }}</div>
                        <template v-if="q.question_type === 'scale'">
                          <div><span class="text-muted">Escala:</span>
                            {{ q.scale_min }}{{ q.scale_min_label ? ' — ' + q.scale_min_label : '' }}
                            até
                            {{ q.scale_max }}{{ q.scale_max_label ? ' — ' + q.scale_max_label : '' }}
                          </div>
                        </template>
                        <template v-else-if="q.dynamic_options">
                          <div class="text-muted mb-1">Opções:</div>
                          <div class="ml-3 text-muted font-italic">Carregadas dos projetos cadastrados + "Outro"</div>
                        </template>
                        <template v-else-if="q.options && q.options.length">
                          <div class="text-muted mb-1">Opções:</div>
                          <div v-for="opt in q.options" :key="opt.id" class="ml-3">{{ opt.option_text }}</div>
                          <div v-if="q.allows_other" class="ml-3 text-muted">Outro (especifique)</div>
                        </template>
                      </div>
                    </div>
                  </div>

                  <!-- Avaliação -->
                  <h6 class="text-uppercase text-muted mt-4 mb-3" style="font-size:0.72rem;letter-spacing:0.06em">
                    <i class="fa fa-star mr-1"></i> Avaliação
                  </h6>
                  <div v-for="(q, i) in perguntasAvaliacao" :key="q.code" class="card mb-2">
                    <div class="card-body py-3 px-3">
                      <p class="mb-3 font-weight-bold">{{ i + 1 }}. {{ q.question_text }}</p>
                      <div class="small">
                        <div class="mb-1"><span class="text-muted">Tipo da questão:</span> {{ tipoLabel(q.question_type) }}</div>
                        <div class="mb-1"><span class="text-muted">Obrigatória:</span> {{ q.is_required ? 'Sim' : 'Não' }}</div>
                        <template v-if="q.question_type === 'scale'">
                          <div><span class="text-muted">Escala:</span>
                            {{ q.scale_min }}{{ q.scale_min_label ? ' — ' + q.scale_min_label : '' }}
                            até
                            {{ q.scale_max }}{{ q.scale_max_label ? ' — ' + q.scale_max_label : '' }}
                          </div>
                        </template>
                        <template v-else-if="q.options && q.options.length">
                          <div class="text-muted mb-1">Opções:</div>
                          <div v-for="opt in q.options" :key="opt.id" class="ml-3">{{ opt.option_text }}</div>
                          <div v-if="q.allows_other" class="ml-3 text-muted">Outro (especifique)</div>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </b-tab>

              <!-- ── Aba 3: Perguntas Extras ──────────────────────────────── -->
              <b-tab title="Perguntas Extras">
                <div class="d-flex justify-content-between align-items-start mb-3">
                  <p class="mb-0 text-muted" style="max-width:600px">
                    As <strong>{{ baseQuestions.length }} perguntas base</strong> do sistema são sempre exibidas automaticamente.
                    Adicione aqui perguntas específicas do município.
                  </p>
                  <div class="d-flex flex-shrink-0 ml-3">
                    <b-form-checkbox
                      v-if="temPerguntasArquivadas"
                      v-model="mostrarArquivadas"
                      class="mr-3 mt-1"
                      size="sm"
                    >
                      <small>Mostrar arquivadas</small>
                    </b-form-checkbox>
                    <button class="btn btn-success btn-sm" @click="abrirNovaPergunta">
                      <i class="fa fa-plus mr-1"></i> Nova pergunta
                    </button>
                  </div>
                </div>

                <div v-if="!form.custom_questions || !form.custom_questions.length" class="text-muted small py-2">
                  Nenhuma pergunta extra cadastrada ainda.
                </div>

                <template v-else>
                  <!-- Identificação -->
                  <template v-if="extrasIdentificacao.length">
                    <h6 class="text-uppercase text-muted mb-3" style="font-size:0.72rem;letter-spacing:0.06em">
                      <i class="fa fa-user mr-1"></i> Identificação
                    </h6>
                    <div v-for="(q, i) in extrasIdentificacao" :key="q.id" class="card mb-2" :class="q.is_active === false ? 'border-secondary' : ''">
                      <div class="card-body py-3 px-3" :class="q.is_active === false ? 'bg-light' : ''">
                        <div class="d-flex justify-content-between align-items-start">
                          <p class="mb-3 font-weight-bold flex-grow-1 mr-3">
                            {{ i + 1 }}. {{ q.question_text }}
                            <b-badge v-if="q.is_active === false" variant="secondary" class="ml-1" style="font-size:0.65rem">arquivada</b-badge>
                          </p>
                          <div v-if="q.is_active !== false" class="flex-shrink-0">
                            <b-button variant="outline-info" size="sm" class="mr-1" @click="abrirEdicaoPergunta(q)">
                              <i class="fa fa-edit"></i>
                            </b-button>
                            <b-button variant="outline-danger" size="sm" @click="excluirPergunta(q)">
                              <i class="fa fa-trash"></i>
                            </b-button>
                          </div>
                        </div>
                        <div class="small">
                          <div class="mb-1"><span class="text-muted">Tipo da questão:</span> {{ tipoLabel(q.question_type) }}</div>
                          <div class="mb-1"><span class="text-muted">Obrigatória:</span> {{ q.is_required ? 'Sim' : 'Não' }}</div>
                          <template v-if="q.options && q.options.length">
                            <div class="text-muted mb-1">Opções:</div>
                            <div v-for="opt in q.options" :key="opt.id || opt.option_text" class="ml-3">{{ opt.option_text }}</div>
                            <div v-if="q.allows_other" class="ml-3 text-muted">Outro (especifique)</div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </template>

                  <!-- Avaliação -->
                  <template v-if="extrasAvaliacao.length">
                    <h6 class="text-uppercase text-muted mb-3" :class="extrasIdentificacao.length ? 'mt-4' : ''" style="font-size:0.72rem;letter-spacing:0.06em">
                      <i class="fa fa-star mr-1"></i> Avaliação
                    </h6>
                    <div v-for="(q, i) in extrasAvaliacao" :key="q.id" class="card mb-2" :class="q.is_active === false ? 'border-secondary' : ''">
                      <div class="card-body py-3 px-3" :class="q.is_active === false ? 'bg-light' : ''">
                        <div class="d-flex justify-content-between align-items-start">
                          <p class="mb-3 font-weight-bold flex-grow-1 mr-3">
                            {{ i + 1 }}. {{ q.question_text }}
                            <b-badge v-if="q.is_active === false" variant="secondary" class="ml-1" style="font-size:0.65rem">arquivada</b-badge>
                          </p>
                          <div v-if="q.is_active !== false" class="flex-shrink-0">
                            <b-button variant="outline-info" size="sm" class="mr-1" @click="abrirEdicaoPergunta(q)">
                              <i class="fa fa-edit"></i>
                            </b-button>
                            <b-button variant="outline-danger" size="sm" @click="excluirPergunta(q)">
                              <i class="fa fa-trash"></i>
                            </b-button>
                          </div>
                        </div>
                        <div class="small">
                          <div class="mb-1"><span class="text-muted">Tipo da questão:</span> {{ tipoLabel(q.question_type) }}</div>
                          <div class="mb-1"><span class="text-muted">Obrigatória:</span> {{ q.is_required ? 'Sim' : 'Não' }}</div>
                          <template v-if="q.options && q.options.length">
                            <div class="text-muted mb-1">Opções:</div>
                            <div v-for="opt in q.options" :key="opt.id || opt.option_text" class="ml-3">{{ opt.option_text }}</div>
                            <div v-if="q.allows_other" class="ml-3 text-muted">Outro (especifique)</div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </template>
                </template>
              </b-tab>

              <!-- ── Aba 3: Respostas ─────────────────────────────────────── -->
              <b-tab title="Respostas" @click="carregarRespostas">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <span>
                    Total coletado: <strong class="text-primary">{{ totalRespostas }}</strong> respostas
                  </span>
                  <button
                    class="btn btn-success btn-sm d-inline-flex align-items-center"
                    :disabled="exportandoExcel || totalRespostas === 0"
                    @click="exportarExcel"
                  >
                    <i class="fa fa-file-excel mr-1"></i>
                    {{ exportandoExcel ? "Exportando..." : "Exportar Excel" }}
                  </button>
                </div>

                <div v-if="loadingRespostas" class="text-center py-4">
                  <RotateSquare size="40px" />
                </div>

                <div v-else-if="!respostas.length" class="text-muted small py-2">
                  Nenhuma resposta recebida ainda.
                </div>

                <div v-else>
                  <div v-for="(r, i) in respostas" :key="r.id" class="card mb-2">
                    <div class="card-body py-3 px-3">
                      <div class="d-flex justify-content-between align-items-start">
                        <p class="mb-0 font-weight-bold flex-grow-1 mr-3">
                          Resposta {{ skipRespostas + i + 1 }}
                        </p>
                        <div class="flex-shrink-0 d-flex">
                          <button
                            class="btn btn-outline-info btn-sm mr-1 d-inline-flex align-items-center"
                            @click="toggleDetalhe(r.id)"
                          >
                            <i class="fa mr-1" :class="respostaExpandida === r.id ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                            {{ respostaExpandida === r.id ? 'Fechar' : 'Ver respostas' }}
                          </button>
                          <button
                            class="btn btn-outline-danger btn-sm d-inline-flex align-items-center"
                            :disabled="excluindoResposta === r.id"
                            @click="excluirResposta(r)"
                          >
                            <i class="fa fa-trash"></i>
                          </button>
                        </div>
                      </div>
                      <div class="small mt-1">
                        <div class="mb-1"><span class="text-muted">Data:</span> {{ formatarData(r.submitted_at) }}</div>
                        <div><span class="text-muted">Perguntas respondidas:</span> {{ r.answer_count }}</div>
                      </div>

                      <!-- Detalhe expansível -->
                      <div v-if="respostaExpandida === r.id" class="mt-3">
                        <div v-if="loadingDetalhe" class="text-center py-2">
                          <RotateSquare size="30px" />
                        </div>
                        <template v-else-if="detalheResposta[r.id]">
                          <!-- Identificação -->
                          <template v-if="groupedDetalhe(r.id).identificacao.length">
                            <div class="text-uppercase text-muted mb-1 mt-2" style="font-size:0.68rem;letter-spacing:0.06em">
                              <i class="fa fa-user mr-1"></i> Identificação
                            </div>
                            <div
                              v-for="(a, ai) in groupedDetalhe(r.id).identificacao"
                              :key="'id-' + ai"
                              class="border-top pt-2 mt-2 small"
                            >
                              <div class="font-weight-bold mb-1">{{ ai + 1 }}. {{ a.question_text }}</div>
                              <div v-if="a.question_type === 'free_text'" class="text-muted">{{ a.value || '—' }}</div>
                              <div v-else-if="a.question_type === 'scale'" class="text-muted">{{ a.value !== null && a.value !== undefined ? a.value : '—' }}</div>
                              <div v-else>
                                <div v-if="!a.value || !a.value.length" class="text-muted">—</div>
                                <div v-for="(opt, oi) in a.value" :key="oi" class="text-muted">{{ opt }}</div>
                                <div v-if="a.other_text" class="text-muted">Outro: {{ a.other_text }}</div>
                              </div>
                            </div>
                          </template>
                          <!-- Avaliação -->
                          <template v-if="groupedDetalhe(r.id).avaliacao.length">
                            <div class="text-uppercase text-muted mb-1 mt-3" style="font-size:0.68rem;letter-spacing:0.06em">
                              <i class="fa fa-star mr-1"></i> Avaliação
                            </div>
                            <div
                              v-for="(a, ai) in groupedDetalhe(r.id).avaliacao"
                              :key="'av-' + ai"
                              class="border-top pt-2 mt-2 small"
                            >
                              <div class="font-weight-bold mb-1">{{ ai + 1 }}. {{ a.question_text }}</div>
                              <div v-if="a.question_type === 'free_text'" class="text-muted">{{ a.value || '—' }}</div>
                              <div v-else-if="a.question_type === 'scale'" class="text-muted">{{ a.value !== null && a.value !== undefined ? a.value : '—' }}</div>
                              <div v-else>
                                <div v-if="!a.value || !a.value.length" class="text-muted">—</div>
                                <div v-for="(opt, oi) in a.value" :key="oi" class="text-muted">{{ opt }}</div>
                                <div v-if="a.other_text" class="text-muted">Outro: {{ a.other_text }}</div>
                              </div>
                            </div>
                          </template>
                        </template>
                      </div>
                    </div>
                  </div>

                  <div v-if="totalRespostas > limitRespostas" class="d-flex justify-content-between align-items-center mt-3">
                    <button
                      class="btn btn-outline-secondary btn-sm"
                      :disabled="skipRespostas === 0"
                      @click="paginaAnterior"
                    >Anterior</button>
                    <small class="text-muted">
                      {{ skipRespostas + 1 }}–{{ Math.min(skipRespostas + limitRespostas, totalRespostas) }}
                      de {{ totalRespostas }}
                    </small>
                    <button
                      class="btn btn-outline-secondary btn-sm"
                      :disabled="skipRespostas + limitRespostas >= totalRespostas"
                      @click="proximaPagina"
                    >Próxima</button>
                  </div>
                </div>
              </b-tab>

              <!-- ── Aba 4: Relatório ─────────────────────────────────────── -->
              <b-tab title="Relatório" @click="carregarRelatorio">
                <div v-if="loadingRelatorio" class="text-center py-4">
                  <RotateSquare size="40px" />
                </div>

                <div v-else-if="relatorio && relatorio.total_responses > 0">
                  <div class="alert alert-info d-flex align-items-center">
                    <i class="fa fa-bar-chart mr-2"></i>
                    <span>
                      <strong>{{ relatorio.total_responses }}</strong> respostas coletadas
                    </span>
                    <div class="ml-auto d-flex align-items-center" style="gap:8px">
                      <router-link
                        v-if="showPresentationButton"
                        :to="{ name: 'PmauSurveyDashboard' }"
                        class="btn btn-success btn-sm d-inline-flex align-items-center"
                      >
                        <i class="fa fa-play-circle mr-1"></i> Apresentar
                      </router-link>
                      <button class="btn btn-outline-info btn-sm d-inline-flex align-items-center" @click="recarregarRelatorio">
                        <i class="fas fa-sync mr-1"></i> Atualizar
                      </button>
                    </div>
                  </div>

                  <!-- Identificação -->
                  <div v-if="relatorioPorCategoria.identificacao.length" class="mb-1">
                    <h6 class="text-uppercase text-muted mb-3" style="font-size:0.72rem;letter-spacing:0.06em">
                      <i class="fa fa-user mr-1"></i> Identificação
                    </h6>
                    <div v-for="(q, i) in relatorioPorCategoria.identificacao" :key="q.question_id" class="card mb-2">
                      <div class="card-body py-3 px-3">
                        <p class="mb-3 font-weight-bold">
                          {{ i + 1 }}. {{ q.question_text }}
                          <b-badge v-if="q.is_archived" variant="secondary" class="ml-1" style="font-size:0.65rem">arquivada</b-badge>
                        </p>
                        <p v-if="q.question_type === 'scale' && q.scale_min_label && q.scale_max_label" class="small text-muted mb-2">Onde {{ q.scale_min }} significa "{{ q.scale_min_label }}" e {{ q.scale_max }} significa "{{ q.scale_max_label }}".</p>
                        <div class="small">
                          <div class="mb-1"><span class="text-muted">Tipo da questão:</span> {{ tipoLabel(q.question_type) }}</div>
                          <div class="mb-1"><span class="text-muted">Respostas:</span> {{ q.total_answers }}</div>
                        </div>
                        <template v-if="q.question_type === 'scale'">
                          <div class="small mb-2"><span class="text-muted">Média:</span> <strong class="text-primary">{{ q.average_scale }}</strong> / {{ q.scale_max || 5 }}</div>
                          <table class="table table-sm table-bordered mb-0" style="max-width:360px">
                            <thead class="thead-light"><tr><th>Valor</th><th>Respostas</th><th>%</th></tr></thead>
                            <tbody>
                              <tr v-for="d in q.distribution" :key="d.value">
                                <td>{{ d.value }}</td><td>{{ d.count }}</td>
                                <td>{{ q.total_answers > 0 ? Math.round(d.count / q.total_answers * 100) + '%' : '—' }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </template>
                        <template v-else-if="q.question_type === 'free_text'">
                          <p v-if="!q.text_responses || !q.text_responses.length" class="text-muted small mb-0">Sem respostas ainda.</p>
                          <div v-else>
                            <div v-for="(t, ti) in q.text_responses.slice(0, 30)" :key="ti" class="border-bottom py-1 small">{{ t }}</div>
                            <small v-if="q.text_responses.length > 30" class="text-muted mt-1 d-block">… e mais {{ q.text_responses.length - 30 }} respostas</small>
                          </div>
                        </template>
                        <template v-else>
                          <div v-if="q.average_weight" class="small mb-2"><span class="text-muted">Peso médio ponderado:</span> <strong class="text-primary">{{ q.average_weight }}</strong></div>
                          <table class="table table-sm table-bordered mb-0">
                            <thead class="thead-light"><tr><th>Opção</th><th>Respostas</th><th>%</th><th>Peso</th></tr></thead>
                            <tbody>
                              <tr v-for="d in q.distribution" :key="d.option_id">
                                <td>{{ d.option_text }}</td><td>{{ d.count }}</td><td>{{ d.percentage }}%</td><td>{{ d.weight }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </template>
                      </div>
                    </div>
                  </div>

                  <!-- Avaliação -->
                  <div v-if="relatorioPorCategoria.avaliacao.length" :class="relatorioPorCategoria.identificacao.length ? 'mt-4' : ''">
                    <h6 class="text-uppercase text-muted mb-3" style="font-size:0.72rem;letter-spacing:0.06em">
                      <i class="fa fa-star mr-1"></i> Avaliação
                    </h6>
                    <div v-for="(q, i) in relatorioPorCategoria.avaliacao" :key="q.question_id" class="card mb-2">
                      <div class="card-body py-3 px-3">
                        <p class="mb-3 font-weight-bold">
                          {{ i + 1 }}. {{ q.question_text }}
                          <b-badge v-if="q.is_archived" variant="secondary" class="ml-1" style="font-size:0.65rem">arquivada</b-badge>
                        </p>
                        <p v-if="q.question_type === 'scale' && q.scale_min_label && q.scale_max_label" class="small text-muted mb-2">Onde {{ q.scale_min }} significa "{{ q.scale_min_label }}" e {{ q.scale_max }} significa "{{ q.scale_max_label }}".</p>
                        <div class="small">
                          <div class="mb-1"><span class="text-muted">Tipo da questão:</span> {{ tipoLabel(q.question_type) }}</div>
                          <div class="mb-1"><span class="text-muted">Respostas:</span> {{ q.total_answers }}</div>
                        </div>
                        <template v-if="q.question_type === 'scale'">
                          <div class="small mb-2"><span class="text-muted">Média:</span> <strong class="text-primary">{{ q.average_scale }}</strong> / {{ q.scale_max || 5 }}</div>
                          <table class="table table-sm table-bordered mb-0" style="max-width:360px">
                            <thead class="thead-light"><tr><th>Valor</th><th>Respostas</th><th>%</th></tr></thead>
                            <tbody>
                              <tr v-for="d in q.distribution" :key="d.value">
                                <td>{{ d.value }}</td><td>{{ d.count }}</td>
                                <td>{{ q.total_answers > 0 ? Math.round(d.count / q.total_answers * 100) + '%' : '—' }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </template>
                        <template v-else-if="q.question_type === 'free_text'">
                          <p v-if="!q.text_responses || !q.text_responses.length" class="text-muted small mb-0">Sem respostas ainda.</p>
                          <div v-else>
                            <div v-for="(t, ti) in q.text_responses.slice(0, 30)" :key="ti" class="border-bottom py-1 small">{{ t }}</div>
                            <small v-if="q.text_responses.length > 30" class="text-muted mt-1 d-block">… e mais {{ q.text_responses.length - 30 }} respostas</small>
                          </div>
                        </template>
                        <template v-else>
                          <div v-if="q.average_weight" class="small mb-2"><span class="text-muted">Peso médio ponderado:</span> <strong class="text-primary">{{ q.average_weight }}</strong></div>
                          <table class="table table-sm table-bordered mb-0">
                            <thead class="thead-light"><tr><th>Opção</th><th>Respostas</th><th>%</th><th>Peso</th></tr></thead>
                            <tbody>
                              <tr v-for="d in q.distribution" :key="d.option_id">
                                <td>{{ d.option_text }}</td><td>{{ d.count }}</td><td>{{ d.percentage }}%</td><td>{{ d.weight }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else class="text-center py-5 text-muted">
                  <i class="fa fa-bar-chart fa-3x mb-3 d-block"></i>
                  <p>Nenhuma resposta coletada ainda.<br>Ative o formulário e compartilhe o link público.</p>
                  <button class="btn btn-outline-secondary btn-sm d-inline-flex align-items-center" @click="recarregarRelatorio">
                    <i class="fas fa-sync mr-1"></i> Verificar
                  </button>
                </div>
              </b-tab>

            </b-tabs>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Modal: Nova Edição ────────────────────────────────────────────── -->
    <b-modal
      v-model="modalNovaEdicao"
      title="Nova Edição do Formulário"
      ok-title="Criar nova edição"
      cancel-title="Cancelar"
      :ok-disabled="criandoEdicao"
      @ok="criarNovaEdicao"
      @hidden="fecharModalNovaEdicao"
    >
      <div class="alert alert-warning small mb-3">
        <i class="fa fa-exclamation-triangle mr-1"></i>
        Esta ação <strong>encerrará o formulário atual</strong>.
        Ele não aceitará mais novas respostas. As respostas já coletadas serão preservadas.
      </div>
      <div class="form-group">
        <label><strong>Rótulo do período</strong> <span class="text-muted">(opcional)</span></label>
        <b-form-input
          v-model="novaEdicao.period_label"
          placeholder="Ex: 2024-2029"
        />
        <small class="text-muted">Exibido no histórico de edições para facilitar a identificação.</small>
      </div>
      <b-form-checkbox v-model="novaEdicao.copy_questions" class="mt-2">
        Copiar perguntas extras para a nova edição
      </b-form-checkbox>
    </b-modal>

    <!-- ── Modal: Excluir Pergunta ────────────────────────────────────────── -->
    <b-modal
      v-model="modalExcluirPergunta"
      title="Excluir pergunta"
      ok-title="Confirmar"
      cancel-title="Cancelar"
      :ok-disabled="excluindoPergunta"
      @ok="confirmarExclusaoPergunta"
      @hidden="fecharModalExcluirPergunta"
    >
      <p v-if="perguntaParaExcluir" class="font-weight-bold mb-3">
        "{{ perguntaParaExcluir.question_text.slice(0, 80) }}"
      </p>
      <b-form-group label="Como deseja tratar o histórico de respostas desta pergunta?">
        <b-form-radio v-model="modoExclusao" value="archive" class="mb-2">
          <strong>Arquivar</strong>
          <div class="text-muted small">Remove do formulário, mantém as respostas no relatório histórico.</div>
        </b-form-radio>
        <b-form-radio v-model="modoExclusao" value="remove">
          <strong>Remover definitivamente</strong>
          <div class="text-muted small">A pergunta e seus dados históricos deixarão de aparecer em relatórios.</div>
        </b-form-radio>
      </b-form-group>
    </b-modal>

    <!-- ── Modal: Nova / Editar Pergunta ──────────────────────────────────── -->
    <b-modal
      v-model="modalPergunta"
      size="lg"
      :title="perguntaEditando ? 'Editar Pergunta' : 'Nova Pergunta'"
      ok-title="Salvar"
      cancel-title="Cancelar"
      :ok-disabled="salvandoPergunta"
      @ok="salvarPergunta"
      @hidden="fecharModalPergunta"
    >
      <div class="form-group">
        <label><strong>Texto da pergunta</strong></label>
        <b-form-input
          v-model="novaPergunta.question_text"
          placeholder="Ex: Você participa de ações de arborização no bairro?"
        />
      </div>

      <div class="row">
        <div class="col-md-5">
          <div class="form-group">
            <label><strong>Tipo de resposta</strong></label>
            <b-form-select v-model="novaPergunta.question_type" :options="tiposResposta" />
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label><strong>Categoria</strong></label>
            <b-form-select
              v-model="novaPergunta.category"
              :options="[{value:'identification',text:'Identificação'},{value:'evaluation',text:'Avaliação'}]"
            />
          </div>
        </div>
        <div class="col-md-3 d-flex align-items-end pb-3">
          <b-form-checkbox v-model="novaPergunta.is_required">Obrigatória</b-form-checkbox>
        </div>
      </div>

      <!-- Opções (apenas para tipos de escolha) -->
      <div v-if="novaPergunta.question_type !== 'free_text'">
        <label><strong>Opções de resposta</strong></label>
        <small class="text-muted d-block mb-2">
          O <strong>peso</strong> é usado para análise ponderada (0 a 10).
          Maior peso = resposta mais significativa.
        </small>

        <div
          v-for="(opt, i) in novaPergunta.options"
          :key="i"
          class="d-flex align-items-center mb-2"
        >
          <b-form-input
            v-model="opt.option_text"
            placeholder="Texto da opção"
            class="mr-2"
            size="sm"
          />
          <b-form-input
            v-model.number="opt.weight"
            type="number"
            step="0.5"
            min="0"
            max="10"
            style="width:80px"
            class="mr-2"
            title="Peso"
            size="sm"
          />
          <b-button variant="danger" size="sm" @click="removerOpcao(i)">
            <i class="fa fa-times"></i>
          </b-button>
        </div>

        <button class="btn btn-outline-secondary btn-sm mt-1" @click="adicionarOpcao">
          <i class="fa fa-plus mr-1"></i> Adicionar opção
        </button>

        <div class="mt-3">
          <b-form-checkbox v-model="novaPergunta.allows_other">
            Permitir resposta "Outro (especifique)"
          </b-form-checkbox>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import RotateSquare from "../../components/RotateSquare";
import SurveyService from "../../servico/SurveyService";

const PERGUNTA_VAZIA = () => ({
  question_text: "",
  question_type: "single_choice",
  category: "evaluation",
  is_required: false,
  allows_other: false,
  options: []
});

export default {
  name: "PmauSurvey",
  components: { RotateSquare },
  props: {
    stageCode: {
      type: String,
      default: "participatory_diagnosis"
    },
    headerTitle: {
      type: String,
      default: "Diagnóstico Participativo da Arborização Urbana — Formulário de Percepção Pública"
    },
    publicRoutePrefix: {
      type: String,
      default: "diagnostico"
    },
    downloadFilenamePrefix: {
      type: String,
      default: "diagnostico-participativo-respostas"
    },
    showPresentationButton: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loading: false,
      salvandoForm: false,
      form: {
        id: null,
        title: "",
        description: "",
        status: "draft",
        public_token: "",
        custom_questions: [],
        edition_number: 1,
        period_label: null,
        is_current: true
      },
      formId: null,

      // Modal pergunta
      modalPergunta: false,
      salvandoPergunta: false,
      perguntaEditando: null,
      novaPergunta: PERGUNTA_VAZIA(),
      tiposResposta: [
        { value: "single_choice", text: "Escolha única" },
        { value: "multiple_choice", text: "Múltipla escolha" },
        { value: "free_text", text: "Texto livre" }
      ],

      // Modal exclusão de pergunta
      modalExcluirPergunta: false,
      perguntaParaExcluir: null,
      modoExclusao: "archive",
      excluindoPergunta: false,

      // Status
      statusOpcoes: [
        { value: "draft", text: "Rascunho" },
        { value: "active", text: "Ativo" },
        { value: "closed", text: "Encerrado" }
      ],

      // Edições
      edicoes: [],
      loadingEdicoes: false,
      modalNovaEdicao: false,
      criandoEdicao: false,
      novaEdicao: { period_label: "", copy_questions: true },

      // Toggle perguntas arquivadas
      mostrarArquivadas: false,

      // Respostas
      loadingRespostas: false,
      respostasCarregadas: false,
      respostas: [],
      totalRespostas: 0,
      skipRespostas: 0,
      limitRespostas: 20,
      respostaExpandida: null,
      loadingDetalhe: false,
      detalheResposta: {},
      excluindoResposta: null,

      // Relatório
      loadingRelatorio: false,
      relatorio: null,
      relatorioCarregado: false,

      // Export
      exportandoExcel: false,

      // Perguntas fixas
      baseQuestions: [],
      loadingBaseQuestions: false
    };
  },
  computed: {
    perguntasIdentificacao() {
      return this.baseQuestions.filter((q) => q.category === "identification");
    },
    perguntasAvaliacao() {
      return this.baseQuestions.filter((q) => q.category === "evaluation");
    },
    extrasIdentificacao() {
      return (this.form.custom_questions || []).filter(
        (q) => q.category === "identification" && (q.is_active !== false || this.mostrarArquivadas)
      );
    },
    extrasAvaliacao() {
      return (this.form.custom_questions || []).filter(
        (q) => q.category === "evaluation" && (q.is_active !== false || this.mostrarArquivadas)
      );
    },
    temPerguntasArquivadas() {
      return (this.form.custom_questions || []).some((q) => q.archived_at !== null && q.archived_at !== undefined);
    },
    relatorioPorCategoria() {
      const qs = this.relatorio && this.relatorio.questions ? this.relatorio.questions : [];
      return {
        identificacao: qs.filter((q) => q.category === "identification"),
        avaliacao: qs.filter((q) => q.category === "evaluation"),
      };
    },
    publicUrl() {
      if (!this.form.public_token) return "";
      const base = window.location.origin + window.location.pathname;
      return base + "#/" + this.publicRoutePrefix + "/" + this.form.public_token;
    }
  },
  beforeMount() {
    this.carregarFormulario();
    this.carregarPerguntasFixas();
    this.carregarEdicoes();
  },
  methods: {
    carregarFormulario() {
      const contaId = this.$store.getters.getAutenticacao?.contaSelecionada?.id;
      if (!contaId) return;
      this.loading = true;
      SurveyService.GetForm(contaId, this.stageCode)
        .then((r) => {
          this.form = r.data;
          this.formId = r.data.id;
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao carregar formulário."], type: "warn", duration: 5000 });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    salvarForm() {
      this.salvandoForm = true;
      SurveyService.UpdateForm(this.formId, {
        title: this.form.title,
        description: this.form.description,
        status: this.form.status
      })
        .then((r) => {
          this.form.status = r.data.status;
          this.$notify({ data: ["Configurações salvas com sucesso."], type: "success", duration: 3000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao salvar configurações."], type: "warn", duration: 5000 });
        })
        .finally(() => {
          this.salvandoForm = false;
        });
    },
    copiarLink() {
      if (!this.publicUrl) return;
      navigator.clipboard.writeText(this.publicUrl).then(() => {
        this.$notify({ data: ["Link copiado para a área de transferência!"], type: "success", duration: 2000 });
      });
    },
    statusLabel(s) {
      return { draft: "Rascunho", active: "Ativo", closed: "Encerrado" }[s] || s;
    },
    statusVariant(s) {
      return { draft: "secondary", active: "success", closed: "danger" }[s] || "secondary";
    },
    tipoBadge(t) {
      return { single_choice: "primary", multiple_choice: "info", free_text: "secondary", scale: "warning" }[t] || "secondary";
    },
    tipoLabel(t) {
      return { single_choice: "Escolha única", multiple_choice: "Múltipla escolha", free_text: "Texto livre", scale: "Escala" }[t] || t;
    },
    formatarData(iso) {
      return iso ? new Date(iso).toLocaleString("pt-BR") : "";
    },

    // ── Perguntas fixas ────────────────────────────────────────────────────
    carregarPerguntasFixas() {
      this.loadingBaseQuestions = true;
      SurveyService.ListBaseQuestions(this.stageCode)
        .then((r) => { this.baseQuestions = r.data; })
        .catch(() => {
          this.$notify({ data: ["Erro ao carregar perguntas fixas."], type: "warn", duration: 5000 });
        })
        .finally(() => { this.loadingBaseQuestions = false; });
    },

    // ── Perguntas extras ───────────────────────────────────────────────────
    abrirNovaPergunta() {
      this.perguntaEditando = null;
      this.novaPergunta = PERGUNTA_VAZIA();
      this.modalPergunta = true;
    },
    abrirEdicaoPergunta(q) {
      this.perguntaEditando = q;
      this.novaPergunta = {
        question_text: q.question_text,
        question_type: q.question_type,
        category: q.category,
        is_required: q.is_required,
        allows_other: q.allows_other,
        options: (q.options || []).map((o) => ({
          option_text: o.option_text,
          weight: o.weight
        }))
      };
      this.modalPergunta = true;
    },
    fecharModalPergunta() {
      this.perguntaEditando = null;
      this.novaPergunta = PERGUNTA_VAZIA();
    },
    adicionarOpcao() {
      this.novaPergunta.options.push({ option_text: "", weight: 1 });
    },
    removerOpcao(i) {
      this.novaPergunta.options.splice(i, 1);
    },
    salvarPergunta(evt) {
      evt.preventDefault();
      if (!this.novaPergunta.question_text.trim()) {
        this.$notify({ data: ["Informe o texto da pergunta."], type: "warn", duration: 3000 });
        return;
      }
      this.salvandoPergunta = true;
      const payload = { ...this.novaPergunta };
      const request = this.perguntaEditando
        ? SurveyService.UpdateQuestion(this.formId, this.perguntaEditando.id, payload)
        : SurveyService.CreateQuestion(this.formId, payload);

      request
        .then((r) => {
          if (this.perguntaEditando) {
            const idx = this.form.custom_questions.findIndex(
              (q) => q.id === this.perguntaEditando.id
            );
            if (idx !== -1) this.$set(this.form.custom_questions, idx, r.data);
          } else {
            this.form.custom_questions.push(r.data);
          }
          this.modalPergunta = false;
          this.$notify({ data: ["Pergunta salva com sucesso."], type: "success", duration: 3000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao salvar pergunta."], type: "warn", duration: 5000 });
        })
        .finally(() => {
          this.salvandoPergunta = false;
        });
    },
    excluirPergunta(q) {
      this.perguntaParaExcluir = q;
      this.modoExclusao = "archive";
      this.modalExcluirPergunta = true;
    },
    fecharModalExcluirPergunta() {
      this.perguntaParaExcluir = null;
      this.modoExclusao = "archive";
    },
    confirmarExclusaoPergunta(evt) {
      evt.preventDefault();
      if (!this.perguntaParaExcluir) return;
      const archive = this.modoExclusao === "archive";
      this.excluindoPergunta = true;
      SurveyService.DeleteQuestion(this.formId, this.perguntaParaExcluir.id, archive)
        .then(() => {
          if (archive) {
            const idx = this.form.custom_questions.findIndex((x) => x.id === this.perguntaParaExcluir.id);
            if (idx !== -1) {
              this.$set(this.form.custom_questions[idx], "is_active", false);
              this.$set(this.form.custom_questions[idx], "archived_at", new Date().toISOString());
            }
            this.$notify({ data: ["Pergunta arquivada. O histórico de respostas foi preservado."], type: "success", duration: 4000 });
          } else {
            this.form.custom_questions = this.form.custom_questions.filter((x) => x.id !== this.perguntaParaExcluir.id);
            this.$notify({ data: ["Pergunta removida definitivamente."], type: "success", duration: 3000 });
          }
          this.modalExcluirPergunta = false;
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao excluir pergunta."], type: "warn", duration: 5000 });
        })
        .finally(() => {
          this.excluindoPergunta = false;
        });
    },

    // ── Edições ────────────────────────────────────────────────────────────
    carregarEdicoes() {
      const contaId = this.$store.getters.getAutenticacao?.contaSelecionada?.id;
      if (!contaId) return;
      this.loadingEdicoes = true;
      SurveyService.ListEditions(contaId, this.stageCode)
        .then((r) => { this.edicoes = r.data; })
        .catch(() => {})
        .finally(() => { this.loadingEdicoes = false; });
    },
    abrirModalNovaEdicao() {
      this.novaEdicao = { period_label: "", copy_questions: true };
      this.modalNovaEdicao = true;
    },
    fecharModalNovaEdicao() {
      this.novaEdicao = { period_label: "", copy_questions: true };
    },
    criarNovaEdicao(evt) {
      evt.preventDefault();
      const contaId = this.$store.getters.getAutenticacao?.contaSelecionada?.id;
      if (!contaId) return;
      this.criandoEdicao = true;
      SurveyService.CreateEdition(contaId, {
        period_label: this.novaEdicao.period_label || null,
        copy_questions: this.novaEdicao.copy_questions
      }, this.stageCode)
        .then(() => {
          this.modalNovaEdicao = false;
          this.$notify({ data: ["Nova edição criada com sucesso!"], type: "success", duration: 4000 });
          this.carregarFormulario();
          this.carregarEdicoes();
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao criar nova edição."], type: "warn", duration: 5000 });
        })
        .finally(() => {
          this.criandoEdicao = false;
        });
    },
    edicaoStatusLabel(s) {
      return { draft: "Rascunho", active: "Ativo", closed: "Encerrado" }[s] || s;
    },
    edicaoStatusVariant(s) {
      return { draft: "secondary", active: "success", closed: "danger" }[s] || "secondary";
    },

    // ── Respostas ──────────────────────────────────────────────────────────
    toggleDetalhe(responseId) {
      if (this.respostaExpandida === responseId) {
        this.respostaExpandida = null;
        return;
      }
      this.respostaExpandida = responseId;
      if (this.detalheResposta[responseId]) return;
      this.loadingDetalhe = true;
      SurveyService.GetResponse(responseId)
        .then((r) => { this.$set(this.detalheResposta, responseId, r.data); })
        .catch(() => { this.$notify({ data: ["Erro ao carregar detalhe da resposta."], type: "warn", duration: 5000 }); })
        .finally(() => { this.loadingDetalhe = false; });
    },
    groupedDetalhe(responseId) {
      const detail = this.detalheResposta[responseId];
      if (!detail) return { identificacao: [], avaliacao: [] };
      const answers = detail.answers || [];
      return {
        identificacao: answers.filter((a) => a.category === "identification"),
        avaliacao: answers.filter((a) => a.category === "evaluation"),
      };
    },
    excluirResposta(r) {
      if (!confirm("Deseja excluir esta resposta? A ação pode ser revertida apenas por administrador de banco de dados.")) return;
      this.excluindoResposta = r.id;
      SurveyService.DeleteResponse(r.id)
        .then(() => {
          this.respostas = this.respostas.filter((x) => x.id !== r.id);
          this.totalRespostas = Math.max(0, this.totalRespostas - 1);
          if (this.respostaExpandida === r.id) this.respostaExpandida = null;
          this.$notify({ data: ["Resposta excluída."], type: "success", duration: 3000 });
        })
        .catch(() => { this.$notify({ data: ["Erro ao excluir resposta."], type: "warn", duration: 5000 }); })
        .finally(() => { this.excluindoResposta = null; });
    },
    carregarRespostas() {
      if (!this.formId) return;
      this.loadingRespostas = true;
      SurveyService.ListResponses(this.formId, this.skipRespostas, this.limitRespostas)
        .then((r) => {
          this.respostas = r.data.items;
          this.totalRespostas = r.data.total;
          this.respostasCarregadas = true;
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao carregar respostas."], type: "warn", duration: 5000 });
        })
        .finally(() => {
          this.loadingRespostas = false;
        });
    },
    proximaPagina() {
      this.skipRespostas += this.limitRespostas;
      this.carregarRespostas();
    },
    paginaAnterior() {
      this.skipRespostas = Math.max(0, this.skipRespostas - this.limitRespostas);
      this.carregarRespostas();
    },

    // ── Relatório ──────────────────────────────────────────────────────────
    carregarRelatorio() {
      if (!this.formId || this.relatorioCarregado) return;
      this.loadingRelatorio = true;
      SurveyService.GetReport(this.formId)
        .then((r) => {
          this.relatorio = r.data;
          this.relatorioCarregado = true;
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao carregar relatório."], type: "warn", duration: 5000 });
        })
        .finally(() => {
          this.loadingRelatorio = false;
        });
    },
    recarregarRelatorio() {
      this.relatorioCarregado = false;
      this.carregarRelatorio();
    },

    // ── Export ─────────────────────────────────────────────────────────────
    exportarExcel() {
      if (!this.formId) return;
      this.exportandoExcel = true;
      SurveyService.ExportResponses(this.formId)
        .then((r) => {
          const blob = new Blob([r.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = this.downloadFilenamePrefix + ".xlsx";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao exportar respostas."], type: "warn", duration: 5000 });
        })
        .finally(() => {
          this.exportandoExcel = false;
        });
    }
  }
};
</script>
